import { useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Legend, LineChart, Line } from 'recharts';
import { Wallet, Save, Loader2, Plus, Trash2, AlertTriangle, Info } from 'lucide-react';
import { toast } from '@/components/ui/sonner';
import SectionPdfExport, { buildColumns } from '@/components/SectionPdfExport';

const fmt = (v) => {
  if (v === undefined || v === null || isNaN(v)) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(v);
};

const Field = ({ label, value, onChange, prefix, placeholder, disabled }) => (
  <div>
    <label className="block text-[10px] text-zinc-500 mb-0.5">{label}</label>
    <div className="relative">
      {prefix && <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] text-zinc-500">{prefix}</span>}
      <input
        type="number"
        step="any"
        value={value ?? ''}
        onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
        placeholder={placeholder}
        disabled={disabled}
        className={`w-full bg-zinc-800/60 border border-zinc-700 rounded text-xs text-zinc-200 font-mono py-1 ${prefix ? 'pl-5' : 'pl-2'} pr-2 focus:border-indigo-500/50 focus:outline-none ${disabled ? 'opacity-40' : ''}`}
      />
    </div>
  </div>
);

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const emptyMonth = (month) => ({
  month,
  accounts_receivable: 0,
  inventory: 0,
  prepaid_expenses: 0,
  other_current_assets: 0,
  accounts_payable: 0,
  accrued_liabilities: 0,
  deferred_revenue: 0,
  other_current_liabilities: 0,
});

export default function WorkingCapitalPage({ data, results, analysis, saveAnalysis, loading }) {
  const wc = results?.working_capital || {};
  const wcData = data?.working_capital || {};

  const [monthlyData, setMonthlyData] = useState(() =>
    wcData.monthly_data?.length > 0
      ? wcData.monthly_data
      : MONTHS.map(m => emptyMonth(m))
  );

  const [adjustments, setAdjustments] = useState(() => wcData.adjustments || {
    aged_ar_over_90: 0,
    slow_moving_inventory: 0,
    related_party_receivables: 0,
    stretched_ap: 0,
    one_time_accruals: 0,
    seasonal_adjustment: 0,
  });

  const [collarPct, setCollarPct] = useState(wcData.collar_pct || 0.05);
  const [closingNwc, setClosingNwc] = useState(wcData.closing_nwc || null);
  const [ttmRevenue, setTtmRevenue] = useState(wcData.ttm_revenue || 0);
  const [ttmCogs, setTtmCogs] = useState(wcData.ttm_cogs || 0);

  const handleSave = useCallback(async () => {
    if (!analysis?.id) return;
    await saveAnalysis({
      ...data,
      name: analysis.name || 'Analysis',
      working_capital: {
        monthly_data: monthlyData,
        adjustments,
        collar_pct: collarPct,
        closing_nwc: closingNwc,
        ttm_revenue: ttmRevenue,
        ttm_cogs: ttmCogs,
      },
    });
  }, [monthlyData, adjustments, collarPct, closingNwc, ttmRevenue, ttmCogs, data, analysis, saveAnalysis]);

  const updateMonth = (idx, field, value) => {
    setMonthlyData(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  };

  const nwcChartData = (wc.monthly_nwc || []).map(m => ({
    month: m.month,
    NWC: m.nwc,
    'Current Assets': m.total_ca,
    'Current Liabilities': -m.total_cl,
  }));

  const hasData = wc.monthly_nwc?.length > 0;
  const hasPlaceholder = monthlyData.every(m => m.accounts_receivable === 0 && m.inventory === 0);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="working-capital-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-zinc-100 font-['Manrope']">Working Capital Analysis</h2>
          <p className="text-sm text-zinc-400 mt-1">Normalized NWC, peg calculation, collar & true-up per SMB M&A methodology</p>
        </div>
        <div className="flex items-center gap-2">
          <SectionPdfExport
            title="Working Capital Analysis"
            analysisName={analysis?.name}
            rows={wc.monthly_nwc || []}
            columns={buildColumns([
              { label: 'Month', key: 'month' },
              { label: 'Current Assets', key: 'total_ca', format: 'currency' },
              { label: 'Current Liab.', key: 'total_cl', format: 'currency' },
              { label: 'NWC', key: 'nwc', format: 'currency' },
            ])}
            summary={{
              title: 'Working Capital Summary',
              items: [
                { label: 'TTM Average NWC', value: fmt(wc.ttm_avg_nwc) },
                { label: 'NWC Peg', value: fmt(wc.peg) },
                { label: 'Collar Range', value: `${fmt(wc.collar_low)} - ${fmt(wc.collar_high)}` },
              ]
            }}
          />
          <Button onClick={handleSave} disabled={loading} className="bg-indigo-600 hover:bg-indigo-500 text-white" data-testid="save-wc-btn">
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save & Calculate
          </Button>
        </div>
      </div>

      {/* Placeholder Notice */}
      {hasPlaceholder && (
        <Card className="bg-zinc-900/60 border-amber-500/20">
          <CardContent className="p-4 flex items-start gap-3">
            <Info className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
            <div className="text-xs text-zinc-400 space-y-1">
              <p className="text-amber-400 font-medium">Working Capital data is a placeholder.</p>
              <p>Enter 12 months of trailing AR, Inventory, Prepaid, AP, and Accrued Liabilities to calculate the normalized NWC peg. If your client hasn't provided monthly balance sheet data, request their monthly AP/AR aging reports, general ledger, or management financials.</p>
              <p>Fields left at $0 indicate data not yet available from the seller.</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Revenue/COGS for efficiency metrics */}
      <Card className="bg-zinc-900/60 border-zinc-800/60">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-zinc-300 font-['Manrope']">TTM Revenue & COGS (for efficiency metrics)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Field label="TTM Revenue" value={ttmRevenue} onChange={setTtmRevenue} prefix="$" placeholder="e.g. 1717739" />
            <Field label="TTM COGS" value={ttmCogs} onChange={setTtmCogs} prefix="$" placeholder="e.g. 1037739" />
            <div>
              <label className="block text-[10px] text-zinc-500 mb-0.5">Collar %</label>
              <div className="flex items-center gap-2">
                <Slider value={[collarPct]} min={0.01} max={0.15} step={0.005} onValueChange={([v]) => setCollarPct(v)} className="flex-1" />
                <span className="font-mono text-xs text-zinc-300 w-12 text-right">&plusmn;{(collarPct * 100).toFixed(1)}%</span>
              </div>
            </div>
            <Field label="Closing NWC (if known)" value={closingNwc} onChange={setClosingNwc} prefix="$" placeholder="Enter at close" />
          </div>
        </CardContent>
      </Card>

      {/* Monthly Data Entry Table */}
      <Card className="bg-zinc-900/60 border-zinc-800/60 overflow-x-auto">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-medium text-zinc-300 font-['Manrope']">Monthly Working Capital Components (TTM)</CardTitle>
            <Badge variant="outline" className="border-zinc-700 text-zinc-400 text-[10px]">12-month trailing</Badge>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px]">
              <thead>
                <tr className="border-b border-zinc-800/60">
                  <th className="text-left text-[10px] text-zinc-500 px-2 py-1.5 w-14">Month</th>
                  <th className="text-right text-[10px] text-zinc-500 px-2 py-1.5">A/R</th>
                  <th className="text-right text-[10px] text-zinc-500 px-2 py-1.5">Inventory</th>
                  <th className="text-right text-[10px] text-zinc-500 px-2 py-1.5">Prepaid</th>
                  <th className="text-right text-[10px] text-zinc-500 px-2 py-1.5">Other CA</th>
                  <th className="text-right text-[10px] text-zinc-500 px-2 py-1.5 border-l border-zinc-800/40">A/P</th>
                  <th className="text-right text-[10px] text-zinc-500 px-2 py-1.5">Accrued</th>
                  <th className="text-right text-[10px] text-zinc-500 px-2 py-1.5">Deferred Rev</th>
                  <th className="text-right text-[10px] text-zinc-500 px-2 py-1.5">Other CL</th>
                </tr>
              </thead>
              <tbody>
                {monthlyData.map((m, idx) => (
                  <tr key={idx} className="border-b border-zinc-800/30 hover:bg-zinc-800/20">
                    <td className="px-2 py-1 text-xs text-zinc-300 font-mono font-medium">{m.month}</td>
                    <td className="px-1 py-0.5"><Field value={m.accounts_receivable} onChange={(v) => updateMonth(idx, 'accounts_receivable', v)} prefix="$" /></td>
                    <td className="px-1 py-0.5"><Field value={m.inventory} onChange={(v) => updateMonth(idx, 'inventory', v)} prefix="$" /></td>
                    <td className="px-1 py-0.5"><Field value={m.prepaid_expenses} onChange={(v) => updateMonth(idx, 'prepaid_expenses', v)} prefix="$" /></td>
                    <td className="px-1 py-0.5"><Field value={m.other_current_assets} onChange={(v) => updateMonth(idx, 'other_current_assets', v)} prefix="$" /></td>
                    <td className="px-1 py-0.5 border-l border-zinc-800/40"><Field value={m.accounts_payable} onChange={(v) => updateMonth(idx, 'accounts_payable', v)} prefix="$" /></td>
                    <td className="px-1 py-0.5"><Field value={m.accrued_liabilities} onChange={(v) => updateMonth(idx, 'accrued_liabilities', v)} prefix="$" /></td>
                    <td className="px-1 py-0.5"><Field value={m.deferred_revenue} onChange={(v) => updateMonth(idx, 'deferred_revenue', v)} prefix="$" /></td>
                    <td className="px-1 py-0.5"><Field value={m.other_current_liabilities} onChange={(v) => updateMonth(idx, 'other_current_liabilities', v)} prefix="$" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Normalization Adjustments */}
      <Card className="bg-zinc-900/60 border-zinc-800/60">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-zinc-300 font-['Manrope']">Normalization Adjustments</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-zinc-500 mb-3">Adjust for non-recurring or non-operational items that distort the trailing NWC average.</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <Field label="Aged A/R (>90 days) to remove" value={adjustments.aged_ar_over_90} onChange={(v) => setAdjustments(p => ({ ...p, aged_ar_over_90: v }))} prefix="$" />
            <Field label="Slow-moving inventory to remove" value={adjustments.slow_moving_inventory} onChange={(v) => setAdjustments(p => ({ ...p, slow_moving_inventory: v }))} prefix="$" />
            <Field label="Related party receivables" value={adjustments.related_party_receivables} onChange={(v) => setAdjustments(p => ({ ...p, related_party_receivables: v }))} prefix="$" />
            <Field label="Stretched A/P (add back)" value={adjustments.stretched_ap} onChange={(v) => setAdjustments(p => ({ ...p, stretched_ap: v }))} prefix="$" />
            <Field label="One-time accruals to remove" value={adjustments.one_time_accruals} onChange={(v) => setAdjustments(p => ({ ...p, one_time_accruals: v }))} prefix="$" />
            <Field label="Seasonal adjustment" value={adjustments.seasonal_adjustment} onChange={(v) => setAdjustments(p => ({ ...p, seasonal_adjustment: v }))} prefix="$" />
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {hasData && (
        <>
          {/* NWC Trend Chart */}
          <Card className="bg-zinc-900/60 border-zinc-800/60">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-zinc-300">Net Working Capital Trend</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={nwcChartData} barGap={2}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                  <XAxis dataKey="month" tick={{ fill: '#71717a', fontSize: 10 }} />
                  <YAxis tick={{ fill: '#71717a', fontSize: 10 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}K`} />
                  <Tooltip contentStyle={{ backgroundColor: '#18181b', border: '1px solid #27272a', borderRadius: '8px', fontSize: '11px' }} />
                  <Legend wrapperStyle={{ fontSize: 10 }} />
                  <Bar dataKey="Current Assets" fill="#6366f1" radius={[2, 2, 0, 0]} />
                  <Bar dataKey="Current Liabilities" fill="#ef4444" radius={[2, 2, 0, 0]} />
                  <ReferenceLine y={wc.peg || 0} stroke="#10b981" strokeDasharray="6 3" label={{ value: 'Peg', fill: '#10b981', fontSize: 10 }} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Peg & Collar Summary */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="bg-zinc-900/60 border-indigo-500/20">
              <CardContent className="p-4">
                <span className="text-xs text-zinc-400">Avg NWC (12m trailing)</span>
                <div className="font-mono text-xl font-bold text-zinc-200 mt-1">{fmt(wc.avg_nwc)}</div>
              </CardContent>
            </Card>
            <Card className="bg-zinc-900/60 border-emerald-500/20">
              <CardContent className="p-4">
                <span className="text-xs text-zinc-400">Normalized NWC Peg</span>
                <div className="font-mono text-xl font-bold text-emerald-400 mt-1">{fmt(wc.peg)}</div>
                <span className="text-[10px] text-zinc-500">After {fmt(wc.adjustments?.total_adjustment)} adj.</span>
              </CardContent>
            </Card>
            <Card className="bg-zinc-900/60 border-zinc-800/60">
              <CardContent className="p-4">
                <span className="text-xs text-zinc-400">Collar Range (&plusmn;{(wc.collar_pct * 100).toFixed(1)}%)</span>
                <div className="font-mono text-sm text-zinc-200 mt-1">{fmt(wc.collar_lower)} — {fmt(wc.collar_upper)}</div>
              </CardContent>
            </Card>
            <Card className={`bg-zinc-900/60 ${wc.true_up !== null && wc.true_up !== 0 ? (wc.true_up > 0 ? 'border-amber-500/20' : 'border-emerald-500/20') : 'border-zinc-800/60'}`}>
              <CardContent className="p-4">
                <span className="text-xs text-zinc-400">True-Up at Closing</span>
                <div className={`font-mono text-xl font-bold mt-1 ${wc.true_up === null ? 'text-zinc-500' : wc.true_up > 0 ? 'text-amber-400' : wc.true_up < 0 ? 'text-emerald-400' : 'text-zinc-300'}`}>
                  {wc.true_up !== null ? (wc.true_up > 0 ? `Seller owes ${fmt(wc.true_up)}` : wc.true_up < 0 ? `Buyer owes ${fmt(Math.abs(wc.true_up))}` : 'No adjustment') : 'Pending close'}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Efficiency Metrics */}
          {wc.efficiency && (
            <Card className="bg-zinc-900/60 border-zinc-800/60">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-zinc-300">Working Capital Efficiency</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <MetricBox label="DSO" value={`${wc.efficiency.dso} days`} sub="Days Sales Outstanding" good={wc.efficiency.dso <= 45} />
                  <MetricBox label="DIO" value={`${wc.efficiency.dio} days`} sub="Days Inventory Outstanding" good={wc.efficiency.dio <= 90} />
                  <MetricBox label="DPO" value={`${wc.efficiency.dpo} days`} sub="Days Payable Outstanding" good={wc.efficiency.dpo <= 60} />
                  <MetricBox label="CCC" value={`${wc.efficiency.ccc} days`} sub="Cash Conversion Cycle" good={wc.efficiency.ccc < 60} />
                  <MetricBox label="NWC/Rev" value={`${(wc.efficiency.nwc_pct_revenue * 100).toFixed(1)}%`} sub="NWC as % of Revenue" good={wc.efficiency.nwc_pct_revenue > 0 && wc.efficiency.nwc_pct_revenue < 0.20} />
                </div>
              </CardContent>
            </Card>
          )}

          {/* WC Flags */}
          {wc.flags?.length > 0 && (
            <Card className="bg-zinc-900/60 border-zinc-800/60">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-zinc-300">Working Capital Findings</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-1.5">
                  {wc.flags.map((f, i) => (
                    <div key={i} className={`flex items-start gap-2 text-xs p-2 rounded border ${f.type === 'positive' ? 'border-emerald-500/20 bg-emerald-500/5' : 'border-red-500/20 bg-red-500/5'}`}>
                      <span className={`w-1.5 h-1.5 rounded-full mt-1 shrink-0 ${f.type === 'positive' ? 'bg-emerald-400' : 'bg-red-400'}`} />
                      <span className="text-zinc-300">{f.message}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Methodology note */}
      <Card className="bg-zinc-900/60 border-zinc-800/60">
        <CardContent className="p-4 text-xs text-zinc-400 space-y-1">
          <p className="text-zinc-200 font-medium">Methodology: Normalized Working Capital Peg</p>
          <p>NWC = (AR + Inventory + Prepaid + Other CA) - (AP + Accrued + Deferred Rev + Other CL)</p>
          <p>Excludes: Cash, short-term debt, owner loans, tax liabilities (non-operational items)</p>
          <p>Peg = 12-month trailing average NWC + normalization adjustments</p>
          <p>Collar: &plusmn;{((collarPct || 0.05) * 100).toFixed(1)}% around peg — no true-up within collar. Outside collar, dollar-for-dollar adjustment.</p>
          <p className="text-amber-400">Missing data? Request monthly AP/AR aging, general ledger, or management financials from seller.</p>
        </CardContent>
      </Card>
    </div>
  );
}

function MetricBox({ label, value, sub, good }) {
  return (
    <div className={`p-3 rounded-lg border ${good ? 'border-emerald-500/20 bg-emerald-500/5' : 'border-amber-500/20 bg-amber-500/5'}`}>
      <div className="text-xs text-zinc-400">{label}</div>
      <div className={`font-mono text-lg font-semibold mt-0.5 ${good ? 'text-emerald-400' : 'text-amber-400'}`}>{value}</div>
      <div className="text-[10px] text-zinc-500 mt-0.5">{sub}</div>
    </div>
  );
}
